import {
	useGlobalization,
	StylovyzeComposableDialog,
	Button,
	useUnits,
	error,
} from '@innovyze/stylovyze';
import { DialogProps } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	getCountResolved,
	setAttributeDataGrid,
	setAttributeRangeUnitValues,
	setAttributeRangeValues,
	setCountIsLoading,
	updateAttribute,
} from '@Actions/index';
import {
	AttributeDataGridRow,
	AttributeOption,
	Metadata,
	UnitMetadata,
	WebSocketData,
} from '@Types';
import RangeDataGrid, {
	updateRangeAttributeCounts,
} from './RangeDataGrid/RangeDataGrid';
import {
	selectAttributeDataGrid,
	selectCount,
	selectIsCountLoading,
} from '@Selectors/Risk.selectors';
import { useWebSockets } from '@innovyze/lib-am-common/Utils';
import UniqueDataGrid, {
	updateUniqueAttributeCounts,
} from './UniqueDataGrid/UniqueDataGrid';
import { getUniqueAttributeLabel } from '@Utils/ags';
import { MEASUREMENT_SYSTEM } from '@Utils/units/unitsMappingValues';

interface Props {
	assetType: string;
	option: AttributeOption | undefined;
	onClose: () => void;
	createDefaultRangeValues: (
		newRanges: number,
		unitMetadata?: UnitMetadata,
	) => Metadata[] | undefined;
}

export const EditAttribute = ({
	assetType,
	option,
	onClose,
	createDefaultRangeValues,
}: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const {
		getSystemValueNoIntlFormat,
		getStandardisedValue,
		getSystemUnit,
		system,
	} = useUnits();

	// const [validConfig, setValidConfig] = useState<boolean>(false);
	const [changed, setChanged] = useState(false);
	const [ranges, setRanges] = React.useState<number>(
		option && option.config && option.config.metadata?.length
			? option.config.metadata.length - 2
			: 3,
	);
	const [isUnique, setIsUnique] = React.useState(true);

	const attributeCounts = selectCount(
		`${changed ? 'changed-' : ''}${assetType}-${option?.key}`,
	);

	const isCountLoading = selectIsCountLoading(
		`${changed ? 'changed-' : ''}${assetType}-${option?.key}`,
	);
	// const [requestedCountMetadata, setRequestedCountMetadata] =
	// 	React.useState<Metadata[]>();
	const rows = selectAttributeDataGrid();

	const webSocket = useWebSockets();

	const attributeCountCallBack = async (data: WebSocketData) => {
		const key = data.payload.key;
		const response = data.payload.payload;
		dispatch(
			getCountResolved({
				key: key,
				values: response,
			}),
		);
		dispatch(
			setCountIsLoading({
				key: key,
				loading: false,
			}),
		);
	};

	const attributeCountErrorCallBack = async (data: WebSocketData) => {
		dispatch(
			setCountIsLoading({
				key: data.requestBody.key,
				loading: false,
			}),
		);
	};

	webSocket.setupRoutes(
		'riskCalcCount',
		attributeCountCallBack,
		attributeCountErrorCallBack,
	);

	const createRows = (
		changedRange: boolean,
		metadataType?: string,
		metadata?: Metadata[],
		unitMetadata?: UnitMetadata,
	) => {
		if (metadata) {
			let newRows: AttributeDataGridRow[] = [];
			let fetchCounts =
				attributeCounts === undefined || attributeCounts.length === 0;

			const isUnique = metadataType === 'unique';
			if (isUnique) {
				const sorted = [...metadata];

				// Sort forcing the '--' (null entry) to the bottom
				sorted.sort((a, b) => {
					if (a.key === '--') return 1;
					if (b.key === '--') return -1;
					return a.key.toString().localeCompare(b.key.toString());
				});

				if (sorted[sorted.length - 1].key !== '--') {
					// Add the null score
					sorted.push({
						value: 0,
						key: '--',
					});
				}

				let index = 0;
				newRows = sorted.map(row => {
					let group;
					if (row.group) {
						group = row.group.map(group => ({
							id: index++,
							value: group.key,
							excluded: group.excluded,
						}));
					}

					return {
						id: index++,
						value: row.key,
						excluded: row.excluded,
						grouped: group,
					};
				});

				// Verify the counts
				if (attributeCounts && !fetchCounts) {
					for (
						let i = 0;
						i < newRows.length - 1 && !fetchCounts;
						i++
					) {
						if (newRows[i].grouped) {
							const groups = newRows[i].grouped ?? [];
							for (let i2 = 0; i2 < groups.length - 1; i2++) {
								if (
									!attributeCounts.find(
										({ key }) =>
											(key === undefined || key === null
												? '--'
												: key.toString()) ===
											groups[i2].value.toString(),
									)
								) {
									fetchCounts = true;
									break;
								}
							}
						} else if (
							!attributeCounts.find(
								({ key }) =>
									(key === undefined || key === null
										? '--'
										: key.toString()) ===
									newRows[i].value.toString(),
							)
						) {
							fetchCounts = true;
							break;
						}
					}

					if (!fetchCounts) {
						newRows = updateUniqueAttributeCounts(
							newRows,
							attributeCounts,
							system == MEASUREMENT_SYSTEM.METRIC,
						);
					}
				}

				dispatch(setAttributeDataGrid(newRows));
				dispatch(setAttributeRangeUnitValues(undefined));
				dispatch(setAttributeRangeValues([]));
			} else if (metadata.length !== 0) {
				newRows = metadata.map((row, index) => ({
					id: index,
					value: row.key,
				}));

				if (newRows[newRows.length - 1].value !== '--') {
					// Add the null score
					newRows.push({
						id: newRows.length,
						value: '--',
					});
				}

				if (attributeCounts && !fetchCounts) {
					// Veify they actually match just in case
					for (let i = 0; i < newRows.length - 1; i++) {
						if (
							!attributeCounts.find(
								({ key }) =>
									(key === undefined || key === null
										? '--'
										: key.toString()) === newRows[i].value,
							)
						) {
							fetchCounts = true;
							break;
						}
					}

					if (!fetchCounts) {
						newRows = updateRangeAttributeCounts(
							newRows,
							attributeCounts,
							system == MEASUREMENT_SYSTEM.METRIC,
						);
					}
				}

				dispatch(setAttributeDataGrid(newRows));

				// Extract the range value, ignoring the null
				let rangeValues: number[] = [newRows.length - 1];
				for (let i = 0; i < newRows.length - 1; i++) {
					const values = newRows[i].value.trim().split(' ');
					if (values.length == 2)
						rangeValues[i] = parseFloat(values[1]);
					else rangeValues[i] = parseFloat(values[2]);
				}

				// If we have a unit we may need to do some covnersion stuff later
				if (
					system !== MEASUREMENT_SYSTEM.METRIC &&
					unitMetadata &&
					unitMetadata.unit &&
					unitMetadata.unit.length !== 0
				) {
					const precision = unitMetadata.precision ?? 0;
					// Create an array with the values in the system display value what ever that might be
					const rangeUnitValues = rangeValues.map(value => {
						return parseFloat(
							parseFloat(
								getSystemValueNoIntlFormat(
									`${value} ${unitMetadata.unit}`,
								),
							).toFixed(precision),
						);
					});
					// Now convert the rounded conversion back to metric.... I know !!!
					const internalUnit = getSystemUnit(unitMetadata.unit);
					let adjustment = 1;
					// mm -> in -> cm
					if (unitMetadata.unit === 'mm') adjustment = 10;

					rangeValues = rangeUnitValues.map(score => {
						return parseFloat(
							(
								parseFloat(
									getStandardisedValue(
										`${score} ${internalUnit}`,
									),
								) * adjustment
							).toFixed(precision),
						);
					});
					dispatch(setAttributeRangeUnitValues(rangeUnitValues));
				} else {
					dispatch(setAttributeRangeUnitValues(undefined));
				}

				dispatch(setAttributeRangeValues(rangeValues));
			}

			// Fetch the counts and lengths
			if (fetchCounts) {
				recalculateCount(
					newRows,
					isUnique,
					changedRange,
					option?.config?.metadataType ?? '',
				);
			}
			setIsUnique(isUnique);
		}
	};

	const recalculateCount = (
		rows: AttributeDataGridRow[],
		isUnique: boolean,
		changedRange: boolean,
		metadataType: string,
	) => {
		if (rows && !isCountLoading) {
			let requestMetadata = option?.config?.metadata ?? [];
			if (rows && rows.length !== 0) {
				if (isUnique) {
					requestMetadata = [];

					rows.map(row => {
						if (row.grouped) {
							row.grouped.forEach(group => {
								requestMetadata.push({
									key: group.value,
									value: 0,
									ix: group.id,
								});
							});
						} else {
							requestMetadata.push({
								key: row.value,
								value: 0,
								ix: row.id,
							});
						}
					});
				} else {
					requestMetadata = rows
						.filter(row => row.value !== '--')
						.map(row => ({
							key: row.value,
							value: 0,
							ix: row.id,
						}));
				}
			}

			let key = `${
				changedRange || changed ? 'changed-' : ''
			}${assetType}-${option?.key}`;
			key = key === '' ? `${assetType}-${option?.key}` : key;

			// setRequestedCountMetadata(requestMetadata);

			// Use the name to check if the configuration has been saved and if so then we can use the changedRangesKey if changed
			dispatch(
				getCountResolved({
					key: key,
					values: undefined,
				}),
			);
			dispatch(
				setCountIsLoading({
					key: key,
					loading: true,
				}),
			);
			webSocket.sendMessage(
				JSON.stringify({
					path: 'risk',
					action: 'riskCalcCount',
					key: key,
					metadata: requestMetadata,
					metadataType: metadataType,
					assetType: assetType,
					assetFilterId: '',
					fieldName: option?.key ?? '',
					fieldType: 'attribute',
				}),
			);
		}
	};

	const onRecalculateCount = () => {
		recalculateCount(
			rows,
			isUnique,
			changed,
			option?.config?.metadataType ?? 'unique',
		);
	};

	useEffect(() => {
		if (attributeCounts && attributeCounts.length !== 0) {
			let newRows;
			if (isUnique) {
				newRows = updateUniqueAttributeCounts(
					rows,
					attributeCounts,
					system == MEASUREMENT_SYSTEM.METRIC,
				);
			} else {
				newRows = updateRangeAttributeCounts(
					rows,
					attributeCounts,
					system === MEASUREMENT_SYSTEM.METRIC,
				);
			}
			dispatch(setAttributeDataGrid(newRows));
		}
	}, [attributeCounts]);

	useEffect(() => {
		// TODO
		if (option && option.config) {
			const config = option.config;
			createRows(
				false,
				config.metadataType,
				config.metadata,
				config.unitMetadata,
			);
		}
	}, [option]);

	const onUniqueValuesChanged = () => {
		setChanged(true);
		// setComponentChanged(true);
		// if (onChange) onChange(true, false, false);
	};

	const onRangeValuesChanged = () => {
		setChanged(true);
		// setComponentChanged(true);
		// if (onChange) onChange(true, false, false);
	};

	const onSetRanges = (ranges: number) => {
		if (option && option.config) {
			const config = option.config;
			setChanged(true);
			// setComponentChanged(true);
			// if (onChange) onChange(true, false, false);
			setRanges(ranges);
			const newMetadata = createDefaultRangeValues(
				ranges,
				config.unitMetadata,
			);
			createRows(
				true,
				config.metadataType,
				newMetadata,
				config.unitMetadata,
			);
		}
	};

	const handleCancel = () => {
		onClose();
	};

	const handleSave = () => {
		if (option && option.config && rows && rows.length !== 0) {
			// Update the attribute, save to redux then close the dialog
			let newLabel = option.config.label;
			let newMetadata = [];
			let valid = false;
			if (isUnique) {
				newMetadata = rows
					.filter(row => row.value !== '--')
					.map(row => {
						if (row.grouped) {
							const groups = row.grouped.map(group => {
								return {
									key: group.value,
									value: 0,
									ix: group.id,
									excluded: false,
								};
							});
							valid ||= !row.excluded;
							return {
								key: row.value,
								value: 0,
								ix: row.id,
								excluded: row.excluded,
								group: groups,
							};
						} else {
							valid ||= !row.excluded;
							return {
								key: row.value,
								value: 0,
								ix: row.id,
								excluded: row.excluded,
							};
						}
					});

				newLabel = getUniqueAttributeLabel(newMetadata);
			} else {
				valid = true;
				newMetadata = rows
					.filter(row => row.value !== '--')
					.map(row => ({
						key: row.value,
						value: 0,
						ix: row.id,
					}));
			}

			if (valid) {
				const newConfig = {
					...option.config,
					label: newLabel,
					metadata: newMetadata,
				};
				const newOption = { ...option, config: newConfig };
				dispatch(updateAttribute(newOption));
				onClose();
			} else {
				dispatch(error(t('At least one value should be included')));
			}
		}
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: option !== undefined,
		fullWidth: true,
		maxWidth: 'xl',
		onClose: onCloseDialog,
	};

	return (
		<StylovyzeComposableDialog
			dialogProps={dialogProps}
			data-cy="new-dialog"
			title={t('Edit {{name}}', { name: option?.value })}
			open={option !== undefined}
			width="100%"
			footer={
				<>
					<Button
						onClick={handleCancel}
						variant="contained"
						color="secondary">
						{t('Cancel')}
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={!changed || isCountLoading}>
						{t('Save')}
					</Button>
				</>
			}
			onCancel={handleCancel}>
			{option && !isUnique ? (
				<RangeDataGrid
					key={Date.now()}
					countKey={`${changed ? 'changed-' : ''}${assetType}-${
						option?.key
					}`}
					option={option}
					ranges={ranges}
					onSetRanges={onSetRanges}
					onRangeValuesChanged={onRangeValuesChanged}
					onRecalculateCount={onRecalculateCount}
				/>
			) : (
				[]
			)}
			{option && isUnique ? (
				<UniqueDataGrid
					key={Date.now()}
					countKey={`${changed ? 'changed-' : ''}${assetType}-${
						option?.key
					}`}
					option={option}
					isMetric={system == MEASUREMENT_SYSTEM.METRIC}
					onUniqueValuesChanged={onUniqueValuesChanged}
				/>
			) : (
				[]
			)}
		</StylovyzeComposableDialog>
	);
};
